import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Download } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import DataTable from '../../components/common/DataTable';
import Select from '../../components/common/Select';
import StatusBadge from '../../components/common/StatusBadge';
import Badge from '../../components/common/Badge';
import EmptyState from '../../components/common/EmptyState';
import Skeleton from '../../components/common/Skeleton';
import { paymentService } from '../../services/paymentService';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../hooks/useAuth';
import { formatPrice } from '../../utils/priceCalculator';
import { formatDateTime } from '../../utils/formatRelativeTime';
import { exportCsv } from '../../utils/exportCsv';

const PAGE_SIZE = 20;

const PaymentsManagement = () => {
  const { currentStore } = useAuth();
  const { error } = useToast();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        paymentService.getAll({ status: filter || undefined, page, limit: PAGE_SIZE }),
        paymentService.getStats({ status: filter || undefined }),
      ]);
      setRows(list?.rows || []);
      setTotal(list?.count || 0);
      setStats(s);
    } catch (e) {
      error(e.message || 'Erro ao carregar pagamentos');
    } finally {
      setLoading(false);
    }
  }, [filter, page, error]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    {
      key: 'invoiceNumber',
      header: 'Cobrança',
      render: (p) => (
        <div>
          <p className="font-semibold text-app-text">
            {p.invoiceNumber || `#${p.id}`}
          </p>
          {p.description && (
            <p className="text-xs text-app-text-secondary truncate max-w-xs">{p.description}</p>
          )}
        </div>
      ),
    },
    {
      key: 'orderId',
      header: 'Pedido',
      render: (p) =>
        p.orderId ? (
          <Link
            to={`/fornecedor/painel/pedidos/${p.orderId}`}
            className="text-primary hover:underline text-sm"
            onClick={(e) => e.stopPropagation()}
          >
            #{p.orderId}
          </Link>
        ) : (
          <span className="text-xs text-app-text-secondary">—</span>
        ),
    },
    {
      key: 'method',
      header: 'Método',
      render: (p) =>
        p.methodLabel ? (
          <Badge color="neutral" variant="soft" size="sm">
            {p.methodLabel}
          </Badge>
        ) : (
          <span className="text-xs text-app-text-secondary">—</span>
        ),
    },
    {
      key: 'gatewayProvider',
      header: 'Gateway',
      render: (p) => p.gatewayProvider ? (
        <Badge color="neutral" variant="soft" size="sm">
          {String(p.gatewayProvider).replace('-mock', ' (mock)')}
        </Badge>
      ) : (
        <span className="text-xs text-app-text-secondary">—</span>
      )
    },
    {
      key: 'dueDate',
      header: 'Venc./Pago',
      render: (p) => (
        <div className="text-xs">
          {p.paidAt ? (
            <span className="text-success">Pago {formatDateTime(p.paidAt)}</span>
          ) : p.dueDate ? (
            <span>Vence {formatDateTime(p.dueDate)}</span>
          ) : (
            <span className="text-app-text-secondary">—</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (p) => <StatusBadge domain="payment" status={p.status} />,
    },
    {
      key: 'amount',
      header: 'Valor',
      align: 'right',
      render: (p) => <span className="font-bold text-primary">{formatPrice(parseFloat(p.amount))}</span>,
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-app-text">Pagamentos</h1>
          {currentStore && <p className="text-sm text-app-text-secondary">{currentStore.name}</p>}
        </div>
        <div className="flex gap-2">
          <Button
            variant="subtle"
            leftIcon={<Download size={16} />}
            onClick={() =>
              exportCsv({
                filename: 'pagamentos.csv',
                columns: [
                  { key: 'id', header: 'ID' },
                  { key: 'invoiceNumber', header: 'Fatura' },
                  { key: 'orderId', header: 'Pedido' },
                  { key: 'method', header: 'Método' },
                  { key: 'amount', header: 'Valor' },
                  { key: 'status', header: 'Status' },
                  { key: 'dueDate', header: 'Vencimento', format: formatDateTime },
                  { key: 'paidAt', header: 'Pago em', format: formatDateTime },
                ],
                rows,
              })
            }
            disabled={!rows.length}
          >
            Exportar CSV
          </Button>
          <Select
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setPage(1);
            }}
            options={[
              { value: '', label: 'Todos status' },
              { value: 'pending', label: 'Pendente' },
              { value: 'paid', label: 'Pago' },
              { value: 'overdue', label: 'Vencido' },
              { value: 'cancelled', label: 'Cancelado' },
              { value: 'failed', label: 'Falhou' },
              { value: 'refunded', label: 'Estornado' },
            ]}
          />
        </div>
      </div>

      {loading && !stats ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="block" height={80} />
          ))}
        </div>
      ) : stats ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card className="p-4">
            <p className="text-xs text-app-text-secondary">Total de cobranças</p>
            <p className="text-2xl font-extrabold text-app-text">{stats.total || 0}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-app-text-secondary">Pago</p>
            <p className="text-xl font-extrabold text-success">{formatPrice(stats.totalPaid || 0)}</p>
            <p className="text-xs text-app-text-secondary">{stats.paid || 0} pagamentos</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-app-text-secondary">Pendente</p>
            <p className="text-xl font-extrabold text-warning">{formatPrice(stats.totalPending || 0)}</p>
            <p className="text-xs text-app-text-secondary">{stats.pending || 0} pagamentos</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-app-text-secondary">Vencido</p>
            <p className="text-xl font-extrabold text-error">{formatPrice(stats.totalOverdue || 0)}</p>
            <p className="text-xs text-app-text-secondary">{stats.overdue || 0} pagamentos</p>
          </Card>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        empty={
          <EmptyState
            icon={DollarSign}
            title="Nenhuma cobrança"
            description="Quando um pedido for aprovado e gerar cobrança, aparece aqui."
          />
        }
      />
    </div>
  );
};

export default PaymentsManagement;
